import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/features/auth'
import type { superAdmin } from '@repo/shared'

type Alert = superAdmin.Alert
type AlertType = superAdmin.AlertType
type AlertSeverity = superAdmin.AlertSeverity
type AlertStatus = superAdmin.AlertStatus

interface ListAlertsParams {
  page?: number
  page_size?: number
  severity?: AlertSeverity
  type?: AlertType
  status?: AlertStatus
  organization_id?: string
}

interface AlertsResponse {
  data: Alert[]
  pagination: {
    page: number
    page_size: number
    total: number
    total_pages: number
  }
}

interface AlertSummary {
  active: number
  acknowledged: number
  critical: number
  warning: number
}

export function useAlerts(params: ListAlertsParams = {}) {
  const { page = 1, page_size = 20, severity, type, status, organization_id } = params

  return useQuery({
    queryKey: ['super-admin', 'alerts', params],
    queryFn: async (): Promise<AlertsResponse> => {
      let query = supabase
        .from('alerts')
        .select('*, organizations(name)', { count: 'exact' })
        .order('created_at', { ascending: false })

      if (severity) query = query.eq('severity', severity)
      if (type) query = query.eq('type', type)
      if (status) query = query.eq('status', status)
      if (organization_id) query = query.eq('organization_id', organization_id)

      // Pagination
      const from = (page - 1) * page_size
      const to = from + page_size - 1
      query = query.range(from, to)

      const { data, error, count } = await query

      if (error) throw error

      const alerts: Alert[] = (data || []).map((alert: any) => ({
        id: alert.id,
        type: alert.type,
        severity: alert.severity,
        title: alert.title,
        message: alert.message,
        status: alert.status,
        organization_id: alert.organization_id,
        organization_name: alert.organizations?.name || null,
        acknowledged_at: alert.acknowledged_at,
        acknowledged_by: alert.acknowledged_by,
        resolved_at: alert.resolved_at,
        resolved_by: alert.resolved_by,
        resolution_notes: alert.resolution_notes,
        metadata: alert.metadata || {},
        created_at: alert.created_at,
      }))

      const total = count || 0
      const total_pages = Math.ceil(total / page_size)

      return {
        data: alerts,
        pagination: { page, page_size, total, total_pages },
      }
    },
    refetchInterval: 30000, // Refetch every 30 seconds for real-time alerts
  })
}

export function useAlert(id: string) {
  return useQuery({
    queryKey: ['super-admin', 'alert', id],
    queryFn: async (): Promise<Alert> => {
      const { data: alert, error } = await supabase
        .from('alerts')
        .select('*, organizations(name)')
        .eq('id', id)
        .single()

      if (error) throw error

      return {
        id: alert.id,
        type: alert.type,
        severity: alert.severity,
        title: alert.title,
        message: alert.message,
        status: alert.status,
        organization_id: alert.organization_id,
        organization_name: alert.organizations?.name || null,
        acknowledged_at: alert.acknowledged_at,
        acknowledged_by: alert.acknowledged_by,
        resolved_at: alert.resolved_at,
        resolved_by: alert.resolved_by,
        resolution_notes: alert.resolution_notes,
        metadata: alert.metadata || {},
        created_at: alert.created_at,
      }
    },
    enabled: !!id,
  })
}

export function useAlertSummary() {
  return useQuery({
    queryKey: ['super-admin', 'alerts', 'summary'],
    queryFn: async (): Promise<AlertSummary> => {
      // Get counts by status and severity
      const { data: alerts } = await supabase
        .from('alerts')
        .select('status, severity')

      let active = 0
      let acknowledged = 0
      let critical = 0
      let warning = 0

      for (const alert of alerts || []) {
        if (alert.status === 'active') active++
        if (alert.status === 'acknowledged') acknowledged++
        if (alert.severity === 'critical') critical++
        if (alert.severity === 'warning' || alert.severity === 'high') warning++
      }

      return { active, acknowledged, critical, warning }
    },
    refetchInterval: 30000,
  })
}

export function useUnreadAlertCount() {
  return useQuery({
    queryKey: ['super-admin', 'alerts', 'unread-count'],
    queryFn: async (): Promise<number> => {
      const { count } = await supabase
        .from('alerts')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'active')

      return count || 0
    },
    refetchInterval: 30000,
  })
}

export function useAcknowledgeAlert() {
  const queryClient = useQueryClient()
  const { userProfile } = useAuth()

  return useMutation({
    mutationFn: async (id: string): Promise<Alert> => {
      const { data: alert, error } = await supabase
        .from('alerts')
        .update({
          status: 'acknowledged',
          acknowledged_at: new Date().toISOString(),
          acknowledged_by: userProfile?.id,
        })
        .eq('id', id)
        .select()
        .single()

      if (error) throw error
      return alert
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['super-admin', 'alerts'] })
    },
  })
}

export function useResolveAlert() {
  const queryClient = useQueryClient()
  const { userProfile } = useAuth()

  return useMutation({
    mutationFn: async ({ id, resolution }: { id: string; resolution: string }): Promise<Alert> => {
      const { data: alert, error } = await supabase
        .from('alerts')
        .update({
          status: 'resolved',
          resolved_at: new Date().toISOString(),
          resolved_by: userProfile?.id,
          resolution_notes: resolution,
        })
        .eq('id', id)
        .select()
        .single()

      if (error) throw error
      return alert
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['super-admin', 'alerts'] })
    },
  })
}

export function useDismissAlert() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string): Promise<Alert> => {
      const { data: alert, error } = await supabase
        .from('alerts')
        .update({ status: 'dismissed' })
        .eq('id', id)
        .select()
        .single()

      if (error) throw error
      return alert
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['super-admin', 'alerts'] })
    },
  })
}
